import { useEffect } from "react";
import LandingPageLayout from "../../layout/LandingPageLayout";
import PageMeta from "../../components/common/common/PageMeta";
import {
  Shield,
  Lock,
  Users,
  Eye,
  Link as LinkIcon,
  Server,
  Trash2,
  Mail,
  Check,
  Key,
  Clock,
  FileText,
} from "lucide-react";

const SecurityPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <LandingPageLayout>
      <PageMeta
        title="Security | Casekeep - How We Protect Your Client Data"
        description="Learn how Casekeep protects sensitive client information with AES-256-GCM encryption, role-based access controls, comprehensive audit logging, and secure file sharing for accountants, CPAs, and legal professionals."
      />

      {/* Hero Section */}
      <section className="py-16 sm:py-24 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-brand-100 dark:bg-brand-950 rounded-xl flex items-center justify-center">
              <Shield className="w-7 h-7 text-brand-600 dark:text-brand-400" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
              Security at Casekeep
            </h1>
          </div>
          <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
            Casekeep is built for professionals who handle sensitive client
            information every day—accountants, attorneys, caseworkers, and
            consultants. That means that the foundation of Casekeep is rooted in
            the best secure practices for your clients. This page explains
            exactly how we protect your data.
          </p>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16 sm:py-20 bg-brand-100 dark:bg-brand-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6">Overview</h2>
          <div className="space-y-4 leading-relaxed dark:text-white">
            <p>
              Casekeep is a secure way to retrieve and collect sensitive data
              for case-based document management. We built it for small
              businesses and independent professionals who need to collect and
              share files without enterprise complexity.
            </p>
            <p>
              We're a small team. That means we move carefully, focus on doing
              things right, and don't make promises we can't keep. We don't
              claim compliance certifications we haven't earned. Instead, we
              focus on implementing strong, practical security controls and
              being transparent about how they work.
            </p>
            <p>
              Our approach:{" "}
              <span className="font-bold">
                encrypt everything at rest, control access tightly, log
                everything, and give you visibility into what's happening with
                your data.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* Encryption Section */}
      <section className="py-16 sm:py-20 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center">
              <Lock className="w-6 h-6 text-brand-600 dark:text-brand-400" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              Encryption
            </h2>
          </div>

          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-8">
            All files, forms, and other sensitive data in Casekeep is encrypted
            using AES-256-GCM: an authenticated encryption standard used widely
            in financial and healthcare applications.
          </p>

          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-8">
            In practical terms, even if someone gained access to stored data, it
            would appear as unreadable ciphertext without the corresponding
            encryption keys. Encryption keys are managed separately via AWS KMS
            and are accessible only through tightly scoped, authenticated
            service permissions.
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Key className="w-5 h-5 text-brand-600 dark:text-brand-400" />
                Data Encryption
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
                We use a data encryption strategy called envelope encryption
                utilizing the AWS Key Management Service (KMS):
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300">
                    Each object gets its own unique 256-bit Data Encryption Key
                    (DEK)
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300">
                    The DEK encrypts the file content using AES-256-GCM
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300">
                    The DEK itself is encrypted by a Customer Master Key (CMK)
                    in AWS KMS
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300">
                    Integrity is verified using authentication tags (GCM mode)
                  </span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Data in Transit
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                All connections to Casekeep use TLS encryption. Data is
                encrypted between your browser and our servers, and between our
                servers and storage providers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Access Controls Section */}
      <section className="py-16 sm:py-20 bg-brand-100 dark:bg-brand-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-brand-600 dark:bg-white/20 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              Access Controls
            </h2>
          </div>

          <p className="text-gray-700 dark:text-brand-100 leading-relaxed mb-8">
            Casekeep uses role-based access control (RBAC) with multiple layers
            of isolation to ensure users only see what they're authorized to
            access.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white dark:bg-white/10 border border-brand-300 dark:border-white/20 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Organization Roles
              </h3>
              <ul className="space-y-2 text-gray-700 dark:text-brand-100">
                <li>
                  <strong className="text-gray-900 dark:text-white">
                    Admin
                  </strong>{" "}
                  — Full access to all cases, files, and settings
                </li>
                <li>
                  <strong className="text-gray-900 dark:text-white">
                    Staff
                  </strong>{" "}
                  — Access only to cases they're assigned to
                </li>
                <li>
                  <strong className="text-gray-900 dark:text-white">
                    Client
                  </strong>{" "}
                  — Access only to their assigned cases via the client portal
                </li>
              </ul>
            </div>

            <div className="bg-white dark:bg-white/10 border border-brand-300 dark:border-white/20 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Data Isolation
              </h3>
              <ul className="space-y-2 text-gray-700 dark:text-brand-100">
                <li>
                  <strong className="text-gray-900 dark:text-white">
                    Organization-level
                  </strong>{" "}
                  — Complete separation between organizations
                </li>
                <li>
                  <strong className="text-gray-900 dark:text-white">
                    Case-level
                  </strong>{" "}
                  — Files and submissions scoped to individual cases
                </li>
                <li>
                  <strong className="text-gray-900 dark:text-white">
                    Folder-level
                  </strong>{" "}
                  — Granular permissions (none, read, modify)
                </li>
              </ul>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Authentication
            </h3>
            <p className="text-gray-700 dark:text-brand-100 leading-relaxed">
              User authentication is handled through Supabase Auth with
              JWT-based sessions. Passwords are hashed using bcrypt. Session
              cookies use HttpOnly, Secure, and SameSite=strict flags to prevent
              common attacks.
            </p>
          </div>
        </div>
      </section>

      {/* Audit Logging Section */}
      <section className="py-16 sm:py-20 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center">
              <Eye className="w-6 h-6 text-brand-600 dark:text-brand-400" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              Audit Logging
            </h2>
          </div>

          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-8">
            Every significant action in Casekeep is logged to provide a complete
            audit trail for compliance and security monitoring.
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                What We Log
              </h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300">
                    File operations: uploads, downloads, deletions
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300">
                    Share link events: creation, access attempts (success and
                    failure), revocation
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300">
                    Form submissions and signature requests
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300">
                    User and IP address for each action
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300">
                    User agent and timestamp
                  </span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Log Access
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Audit logs are scoped to your organization and accessible
                through the dashboard. Logs are stored in append-only fashion
                and can be exported to CSV for compliance reporting. Participant
                email addresses are redacted in logs to protect privacy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* File Sharing & Link Security Section */}
      <section className="py-16 sm:py-20 bg-brand-100 dark:bg-brand-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-brand-600 dark:bg-white/20 rounded-lg flex items-center justify-center">
              <LinkIcon className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              File Sharing & Link Security
            </h2>
          </div>

          <p className="text-gray-700 dark:text-brand-100 leading-relaxed mb-8">
            Share links allow you to securely share files with people outside
            your organization. We've built multiple layers of protection into
            the sharing system.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-white/10 border border-brand-300 dark:border-white/20 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-5 h-5 text-brand-600 dark:text-white" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Expiring Links
                </h3>
              </div>
              <p className="text-gray-700 dark:text-brand-100">
                All share links have a configurable expiration (default 7 days).
                Links automatically stop working after expiration and cannot be
                reactivated.
              </p>
            </div>

            <div className="bg-white dark:bg-white/10 border border-brand-300 dark:border-white/20 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <Mail className="w-5 h-5 text-brand-600 dark:text-white" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Email Verification
                </h3>
              </div>
              <p className="text-gray-700 dark:text-brand-100">
                For sensitive files, require email verification. Recipients must
                verify their email via magic link before accessing the shared
                content.
              </p>
            </div>

            <div className="bg-white dark:bg-white/10 border border-brand-300 dark:border-white/20 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-5 h-5 text-brand-600 dark:text-white" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Download Limits
                </h3>
              </div>
              <p className="text-gray-700 dark:text-brand-100">
                Set a maximum number of downloads per link. Once the limit is
                reached, the link stops working—useful for one-time document
                delivery.
              </p>
            </div>

            <div className="bg-white dark:bg-white/10 border border-brand-300 dark:border-white/20 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <Key className="w-5 h-5 text-brand-600 dark:text-white" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Secure Tokens
                </h3>
              </div>
              <p className="text-gray-700 dark:text-brand-100">
                Share links use 256-bit cryptographically random tokens (64
                characters). Links can be revoked instantly from your dashboard.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Data Storage & Infrastructure Section */}
      <section className="py-16 sm:py-20 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center">
              <Server className="w-6 h-6 text-brand-600 dark:text-brand-400" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              Data Storage & Infrastructure
            </h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Storage
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Encrypted files are stored in Backblaze B2, an S3-compatible
                object storage service. Files are encrypted before upload (as
                described in the Encryption section), so data is protected even
                at the storage layer.
              </p>

              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mt-3">
                Only a few non-sensitive items, such as logos or marketing
                images, are stored unencrypted to allow display in emails and
                links. No client data or sensitive information is ever stored
                this way.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Database & Authentication
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                User accounts and application data are managed through Supabase,
                which provides PostgreSQL database hosting and authentication
                services. Supabase infrastructure runs on AWS.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Key Management
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Encryption keys are managed through AWS Key Management Service
                (KMS). Master keys never leave KMS—only data encryption keys are
                retrieved for use, and those are stored encrypted.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Environment Separation
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Development, staging, and production environments are completely
                separate with their own databases, storage buckets, and
                credentials. Production data is never used in development.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Data Retention & Deletion Section */}
      <section className="py-16 sm:py-20 bg-brand-100 dark:bg-brand-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-brand-600 dark:bg-white/20 rounded-lg flex items-center justify-center">
              <Trash2 className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              Data Retention & Deletion
            </h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Your Control
              </h3>
              <p className="text-gray-700 dark:text-brand-100 leading-relaxed">
                You have full control over your data. Organization admins can
                delete files, cases, and form submissions at any time. When you
                delete a file, it's removed from storage and the encrypted data
                is permanently deleted.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Share Links
              </h3>
              <p className="text-gray-700 dark:text-brand-100 leading-relaxed">
                Share links can be revoked at any time from your dashboard. Once
                revoked, the link immediately stops working. All access attempts
                to revoked links are logged.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Account Deletion
              </h3>
              <p className="text-gray-700 dark:text-brand-100 leading-relaxed">
                If you need to close your account or have questions about data
                deletion, please contact us at{" "}
                <a
                  href="mailto:hello@casekeep.app"
                  className="text-brand-600 dark:text-white hover:underline font-medium"
                >
                  hello@casekeep.app
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Responsible Disclosure Section */}
      <section className="py-16 sm:py-20 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center">
              <Mail className="w-6 h-6 text-brand-600 dark:text-brand-400" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              Responsible Disclosure
            </h2>
          </div>

          <div className="space-y-4 text-gray-600 dark:text-gray-400 leading-relaxed">
            <p>
              Security is a top priority at Casekeep. While our systems are
              designed with strong protections, we welcome responsible
              disclosure of any potential vulnerabilities.
            </p>
            <p>
              Please report security issues to{" "}
              <a
                href="mailto:security@casekeep.app"
                className="text-brand-600 dark:text-brand-400 hover:underline font-medium"
              >
                security@casekeep.app
              </a>
              . We will acknowledge your report within 48 hours and work quickly
              to resolve it. This collaborative approach helps us make the
              internet a safer place.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 sm:py-20 bg-brand-600 dark:bg-brand-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Questions?
          </h2>
          <p className="text-brand-100 leading-relaxed mb-6">
            If you have questions about our security practices or need
            additional information for your compliance requirements, we're happy
            to help.
          </p>
          <a
            href="mailto:hello@casekeep.app"
            className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-brand-600 bg-white rounded-lg hover:bg-brand-50 transition-colors shadow-sm"
          >
            Contact Us
          </a>
        </div>
      </section>
    </LandingPageLayout>
  );
};

export default SecurityPage;
